// src/Payroll_Management/PayRoll_Report/EmployeeMasterReport.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { useNavigate } from "react-router-dom";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Users,
  Search,
  RefreshCcw,
  FileSpreadsheet,
  Printer,
  Hash,
  Type,
  Building2,
  Layers,
  Briefcase,
  Calendar,
  Phone,
  MessageSquareText,
  TrendingUp,
  ReceiptIndianRupee,
  Send,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

function buildPageItems(page, totalPages, maxButtons = 7) {
  // returns array like [1, "...", 8, 9, 10, "...", 25]
  if (totalPages <= maxButtons) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const items = [];
  const siblings = 1; // pages around current
  const first = 1;
  const last = totalPages;

  const left = Math.max(page - siblings, 2);
  const right = Math.min(page + siblings, totalPages - 1);

  items.push(first);

  if (left > 2) items.push("...");

  for (let p = left; p <= right; p++) items.push(p);

  if (right < totalPages - 1) items.push("...");

  items.push(last);

  return items;
}


function safeText(v) {
  return (v ?? "").toString();
}

function toYmd(dateStr) {
  // accepts dd/MM/yyyy or yyyy-MM-dd; returns yyyy-MM-dd (for <input type="date">)
  const s = safeText(dateStr).trim();
  const m1 = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m1) return `${m1[3]}-${m1[2]}-${m1[1]}`;
  const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m2) return s;
  return "";
}

function toDmy(dateStr) {
  // accepts dd/MM/yyyy or yyyy-MM-dd; returns dd/MM/yyyy (ASPX style for API)
  const s = safeText(dateStr).trim();
  const m1 = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m1) return s;
  const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m2) return `${m2[3]}/${m2[2]}/${m2[1]}`;
  return "";
}

const EmployeeMasterReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";

  // NOTE: rights (replacing ASP Session["branchid"], Session["branchtype"])
  // Adjust these keys to match YOUR login storage.
  const headerBranchId = localStorage.getItem("branchId") || "";
  const headerBranchType = localStorage.getItem("branchType") || ""; // "HO" or others

  // meta
  const [metaLoading, setMetaLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [grades, setGrades] = useState([]);
  const [yearsForTds, setYearsForTds] = useState(["Select"]);

  // filters (ASPX equivalents)
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");
  const [site, setSite] = useState("Select"); // ddl_listbranch
  const [deptId, setDeptId] = useState("Select"); // ddl_listdept
  const [gradeId, setGradeId] = useState("Select"); // ddl_grade1 (ASPX not used in query)
  const [desigId, setDesigId] = useState("Select"); // ddl_desig
  const [pfNonPf, setPfNonPf] = useState("Select"); // ddl_pfnonpf
  const [payMode, setPayMode] = useState("Select"); // ddl_paymode
  const [fromDate, setFromDate] = useState(""); // input type=date (we will send dd/MM/yyyy)
  const [toDate, setToDate] = useState(""); // input type=date
  const [tdsYear, setTdsYear] = useState("Select"); // ddl_year
const navigate = useNavigate();

  // autocomplete (simple)
  const [codeSug, setCodeSug] = useState([]);
  const [nameSug, setNameSug] = useState([]);
  const [acLoading, setAcLoading] = useState(false);

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // selection for SendSMS
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // pagination (React)
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-30", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const softBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-bold border shadow-sm transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const headerAxios = () => ({
    headers: {
      "x-finyear-id": Number(finYearId) || "",
      "x-branch-id": headerBranchId ? Number(headerBranchId) : "",
      "x-branch-type": headerBranchType || "",
    },
  });

  const fetchMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    try {
      setMetaLoading(true);
      const res = await axios.get(`${BASE_URL}/api/employee-report/meta`, {
        params: { finYearId: Number(finYearId) },
        ...headerAxios(),
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Meta load failed");
        return;
      }

      const data = res.data;

      setBranches(data.branches || []);
      setDepartments(data.departments || []);
      setDesignations(data.designations || []);
      setGrades(data.grades || []);
      setYearsForTds(data.yearsForTds || ["Select"]);

      // default branch selection like ASP
      const defBranch = data.defaultBranchId ? String(data.defaultBranchId) : "Select";
      setSite(defBranch);

      // if non-HO, keep it locked (handled in UI disabling)
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta load failed");
    } finally {
      setMetaLoading(false);
    }
  };

  const fetchList = async (p = page) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/employee-report`, {
        params: {
          finYearId: Number(finYearId),
          page: p,
          pageSize,

          // filters (match Node route we made)
          site,
          empCode: empCode.trim(),
          empName: empName.trim(),
          deptId,
          gradeId, // currently unused in node (kept)
          desigId,
          pfNonPf,
          payMode,
          fromDate: toDmy(fromDate),
          toDate: toDmy(toDate),
        },
        ...headerAxios(),
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);

        setSelectedIds([]);
        setSelectAll(false);
      } else {
        toast.error(res?.data?.message || "Failed to load employees.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleRefresh = async () => {
    await fetchMeta();
    setPage(1);
    await fetchList(1);
  };

  // Simple autocomplete calls (debounced-ish)
  useEffect(() => {
    let alive = true;
    const run = async () => {
      const q = empCode.trim();
      if (q.length < 1) {
        if (alive) setCodeSug([]);
        return;
      }
      if (!String(finYearId).trim()) return;

      try {
        setAcLoading(true);
        const res = await axios.get(`${BASE_URL}/api/employee-report/autocomplete/empcode`, {
          params: { finYearId: Number(finYearId), prefix: q, site },
          ...headerAxios(),
        });
        if (alive && res?.data?.success) setCodeSug(res.data.items || []);
      } catch {
        // ignore
      } finally {
        if (alive) setAcLoading(false);
      }
    };

    const t = setTimeout(run, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empCode, site]);

  useEffect(() => {
    let alive = true;
    const run = async () => {
      const q = empName.trim();
      if (q.length < 1) {
        if (alive) setNameSug([]);
        return;
      }
      if (!String(finYearId).trim()) return;

      try {
        setAcLoading(true);
        const res = await axios.get(`${BASE_URL}/api/employee-report/autocomplete/empname`, {
          params: { finYearId: Number(finYearId), prefix: q, site },
          ...headerAxios(),
        });
        if (alive && res?.data?.success) setNameSug(res.data.items || []);
      } catch {
        // ignore
      } finally {
        if (alive) setAcLoading(false);
      }
    };

    const t = setTimeout(run, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empName, site]);

  const toggleSelect = (employeeId) => {
    setSelectedIds((prev) =>
      prev.includes(employeeId) ? prev.filter((x) => x !== employeeId) : [...prev, employeeId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }
    setSelectedIds(rows.map((r) => r.fld_Employeeid));
    setSelectAll(true);
  };

  // Actions (ASP opened new windows by setting Session; we use query params)
  const openPopup = (path) => {
    window.open(path, "_blank", "width=900,height=900,resizable=no,scrollbars=yes");
  };

const handlePrintOne = (empId) => {
  openPopup(`/PayRoll/EmployeeReportPrint?empId=${encodeURIComponent(empId)}`);
};


const handleRemarks = (empId) => {
  navigate(`/PayRoll/EmployeeRemarksReport?empId=${encodeURIComponent(empId)}`);
};


const handlePerformance = (empId) => {
  navigate(`/PayRoll/EmployeePerformanceReport?empId=${empId}`);
};


const handleTdsExport = (empId) => {
  if (!String(finYearId).trim()) {
    toast.warn("Please select Financial Year.");
    return;
  }

  if (tdsYear === "Select" || !tdsYear) {
    toast.warn("Please select Year For TDS");
    return;
  }

  // ✅ direct download (server sends excel headers)
  const url =
    `${BASE_URL}/api/employee-report/tds-export` +
    `?finYearId=${encodeURIComponent(finYearId)}` +
    `&empId=${encodeURIComponent(empId)}` +
    `&year=${encodeURIComponent(tdsYear)}`;

  // best for file download
  window.location.href = url;
};


  const handlePrintAll = () => {
    // opens printable report (replace with your route)
    const qs = new URLSearchParams({
      site,
      deptId,
      desigId,
      empCode: empCode.trim(),
      empName: empName.trim(),
      payMode,
      pfNonPf,
      fromDate: toDmy(fromDate),
      toDate: toDmy(toDate),
    }).toString();

    openPopup(`/PayRoll/EmployeeAllReportPrint?${qs}`);
  };

const handleExcelExport = async () => {
  if (!String(finYearId).trim()) {
    toast.warn("Please select Financial Year.");
    return;
  }

  try {
    const res = await axios.get(`${BASE_URL}/api/employee-report/export`, {
      params: {
        finYearId: Number(finYearId),
        site,
        empCode: empCode.trim(),
        empName: empName.trim(),
        deptId,
        desigId,
        pfNonPf,
        payMode,
        fromDate: toDmy(fromDate),
        toDate: toDmy(toDate),
      },
      headers: {
        "x-finyear-id": Number(finYearId),
        "x-branch-id": headerBranchId ? Number(headerBranchId) : "",
        "x-branch-type": headerBranchType || "",
      },
      responseType: "blob",
    });

    const blob = new Blob([res.data], { type: "application/vnd.ms-excel" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "EmployeeMasterReport.xls";
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
    toast.success("Excel exported");
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || "Export failed";
    toast.error(msg);
  }
};


  const handleSendSms = () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select any one");
      return;
    }
    // If you later implement SMS page/route:
    // openPopup(`/SMS/EMPSendSMS?ids=${selectedIds.join(",")}`)
    toast.info("SendSMS not wired yet (needs SMS screen/api).");
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  const branchLocked = headerBranchType && headerBranchType !== "HO";

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE MASTER REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Users className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Employee Master Report
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search / Print / Export / Pagination
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleRefresh} className={ghostBtn} disabled={loading || metaLoading}>
                <RefreshCcw className={`w-4 h-4 ${(loading || metaLoading) ? "animate-spin" : ""}`} />
                Refresh
              </button>

            <button onClick={handleExcelExport} className={softBtn} disabled={loading || total === 0}>
  <FileSpreadsheet className="w-4 h-4" />
  Excel
</button>


              <button onClick={handlePrintAll} className={softBtn} disabled={loading}>
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>

          {/* Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Employee Code */}
              <div>
                <label className={labelBase}>Employee Code</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Hash className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    value={empCode}
                    onChange={(e) => setEmpCode(e.target.value)}
                    placeholder="Type code..."
                    className={`pl-11 ${inputBase}`}
                    list="empCodeList"
                  />
                  <datalist id="empCodeList">
                    {codeSug.slice(0, 10).map((x, i) => (
                      <option value={x} key={i} />
                    ))}
                  </datalist>
                </div>
              </div>

              {/* Employee Name */}
              <div>
                <label className={labelBase}>Employee Name</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Type className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    value={empName}
                    onChange={(e) => setEmpName(e.target.value)}
                    placeholder="Type name..."
                    className={`pl-11 ${inputBase}`}
                    list="empNameList"
                  />
                  <datalist id="empNameList">
                    {nameSug.slice(0, 10).map((x, i) => (
                      <option value={x} key={i} />
                    ))}
                  </datalist>
                </div>
              </div>

              {/* Branch */}
              <div>
                <label className={labelBase}>Branch</label>
                <div className="mt-2 flex items-center gap-2">
                  <Building2 className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select
                    className={inputBase}
                    value={site}
                    onChange={(e) => setSite(e.target.value)}
                    disabled={branchLocked || metaLoading}
                    title={branchLocked ? "Branch locked by user rights" : ""}
                  >
                    <option value="Select">Select</option>
                    {branches.map((b, idx) => {
                      // Branch records shape depends on SP; support common fields
                      const id = b.fld_branchid ?? b.fld_BranchId ?? b.id;
                      const name = b.fld_BranchName ?? b.fld_branchname ?? b.name;
                      if (!id) return null;
                      return (
                        <option key={idx} value={String(id)}>
                          {name || id}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              {/* Department */}
              <div>
                <label className={labelBase}>Department</label>
                <div className="mt-2 flex items-center gap-2">
                  <Layers className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select className={inputBase} value={deptId} onChange={(e) => setDeptId(e.target.value)} disabled={metaLoading}>
                    <option value="Select">Select</option>
                    {departments.map((d) => (
                      <option key={d.id} value={String(d.id)}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Grade (kept because ASPX has it; currently not used in backend query) */}
              <div>
                <label className={labelBase}>Grade</label>
                <div className="mt-2 flex items-center gap-2">
                  <Layers className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select className={inputBase} value={gradeId} onChange={(e) => setGradeId(e.target.value)} disabled={metaLoading}>
                    <option value="Select">Select</option>
                    {grades.map((g) => (
                      <option key={g.id} value={String(g.id)}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Designation */}
              <div>
                <label className={labelBase}>Designation</label>
                <div className="mt-2 flex items-center gap-2">
                  <Briefcase className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select className={inputBase} value={desigId} onChange={(e) => setDesigId(e.target.value)} disabled={metaLoading}>
                    <option value="Select">Select</option>
                    {designations.map((d) => (
                      <option key={d.id} value={String(d.id)}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* PF/NonPF */}
              <div>
                <label className={labelBase}>PF/Non PF</label>
                <select className={inputBase} value={pfNonPf} onChange={(e) => setPfNonPf(e.target.value)}>
                  <option value="Select">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                  <option value="Honorarium">Honorarium</option>
                </select>
              </div>

              {/* Paymode */}
              <div>
                <label className={labelBase}>Payment Mode</label>
                <select className={inputBase} value={payMode} onChange={(e) => setPayMode(e.target.value)}>
                  <option value="Select">Select</option>
                  <option value="Cash">Cash</option>
                  <option value="Bank">Bank</option>
                </select>
              </div>

              {/* From Date */}
              <div>
                <label className={labelBase}>From Date</label>
                <div className="mt-2 flex items-center gap-2">
                  <Calendar className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    type="date"
                    value={toYmd(fromDate)}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputBase}
                  />
                </div>
              </div>

              {/* To Date */}
              <div>
                <label className={labelBase}>To Date</label>
                <div className="mt-2 flex items-center gap-2">
                  <Calendar className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <input
                    type="date"
                    value={toYmd(toDate)}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputBase}
                  />
                </div>
              </div>

              {/* Year for TDS */}
              <div>
                <label className={labelBase}>Year For TDS</label>
                <select className={inputBase} value={tdsYear} onChange={(e) => setTdsYear(e.target.value)}>
                  {(yearsForTds || ["Select"]).map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <button type="submit" className={primaryBtn} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>
            </div>

            {acLoading && (
              <div className={`mt-4 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Loading suggestions...
              </div>
            )}
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Designation</th>
                    <th className="px-4 py-4 text-center font-bold">DOB</th>
                    <th className="px-4 py-4 text-center font-bold">Age</th>
                    <th className="px-4 py-4 text-center font-bold">PF No.</th>
                    <th className="px-4 py-4 text-center font-bold">Account No.</th>
                    <th className="px-4 py-4 text-center font-bold">Qualification</th>
                    <th className="px-4 py-4 text-center font-bold">DOJ</th>
                    <th className="px-4 py-4 text-center font-bold">Gross</th>
                    <th className="px-4 py-4 text-center font-bold">Batta</th>
                    <th className="px-4 py-4 text-center font-bold">Grand Total</th>
                    <th className="px-4 py-4 text-center font-bold">Special HRA</th>
                    <th className="px-4 py-4 text-center font-bold">Conv. Allow.</th>
                    <th className="px-4 py-4 text-center font-bold">PF</th>
                    <th className="px-4 py-4 text-center font-bold">Location</th>
                    <th className="px-4 py-4 text-center font-bold">Print</th>
                    <th className="px-4 py-4 text-center font-bold">Remarks</th>
                    <th className="px-4 py-4 text-center font-bold">Performance</th>
                    <th className="px-4 py-4 text-center font-bold">TDS Print</th>
                    <th className="px-4 py-4 text-center font-bold">
                      <div className="flex items-center justify-center gap-2">
                        Select All
                        <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 22 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={22}
                        className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const empId = r.fld_Employeeid ?? r.fld_employeeid ?? r.EmployeeID ?? r.employeeId;
                      return (
                        <tr
                          key={`${empId}-${idx}`}
                          className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${
                            isDark ? "bg-gray-800" : "bg-gray-50"
                          }`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_Employeecode ?? r.fld_employeecode ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_Employee ?? r.fld_employee ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_designationname ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.DOB ?? r.dob ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.Age ?? r.age ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_pfno ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_accountno ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_qualificationname ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.DOJ ?? r.doj ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_grosssalary ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_dailybata ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.grandTot ?? r.grandtot ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_SpecialHRA ?? r.fld_specialhra ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_conveyanceallowance ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_pf ?? ""}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_BranchName ?? r.fld_branchname ?? ""}
                          </td>

                          {/* Print */}
                          <td className="px-4 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handlePrintOne(empId)}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-white text-sm font-bold bg-gray-700 hover:bg-gray-800 transition shadow"
                            >
                              <Printer className="w-4 h-4" />
                              Print
                            </button>
                          </td>

                          {/* Remarks */}
                          <td className="px-4 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleRemarks(empId)}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-white text-sm font-bold bg-indigo-600 hover:bg-indigo-700 transition shadow"
                            >
                              <MessageSquareText className="w-4 h-4" />
                              Remarks
                            </button>
                          </td>

                          {/* Performance */}
                          <td className="px-4 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handlePerformance(empId)}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-white text-sm font-bold bg-emerald-600 hover:bg-emerald-700 transition shadow"
                            >
                              <TrendingUp className="w-4 h-4" />
                              Performance
                            </button>
                          </td>

                          {/* TDS */}
                          <td className="px-4 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleTdsExport(empId)}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-white text-sm font-bold bg-amber-600 hover:bg-amber-700 transition shadow"
                            >
                              <ReceiptIndianRupee className="w-4 h-4" />
                              TDS
                            </button>
                          </td>

                          {/* Select for SMS */}
                          <td className="px-4 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(empId)}
                              onChange={() => toggleSelect(empId)}
                              className="w-4 h-4"
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom: pagination + send */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {/* Bottom controls: pagination + send */}
<div className="flex flex-wrap items-center gap-3">
  {totalPages > 1 && (
    <div className="inline-flex flex-wrap rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
      <button
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        disabled={page === 1}
        className={`px-4 py-2 text-sm font-bold ${
          page === 1
            ? "text-gray-400 cursor-not-allowed"
            : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
        }`}
      >
        Prev
      </button>

      {buildPageItems(page, totalPages, 7).map((it, idx) =>
        it === "..." ? (
          <span
            key={`dots-${idx}`}
            className={`px-4 py-2 text-sm font-bold ${
              isDark ? "text-gray-400" : "text-gray-500"
            }`}
          >
            ...
          </span>
        ) : (
          <button
            key={it}
            onClick={() => setPage(it)}
            className={`px-4 py-2 text-sm font-bold transition ${
              page === it
                ? `${primaryBtnClass} text-white`
                : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
            }`}
          >
            {it}
          </button>
        )
      )}

      <button
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        disabled={page === totalPages}
        className={`px-4 py-2 text-sm font-bold ${
          page === totalPages
            ? "text-gray-400 cursor-not-allowed"
            : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
        }`}
      >
        Next
      </button>
    </div>
  )}

  <button
    onClick={handleSendSms}
    className={primaryBtn}
    disabled={selectedIds.length === 0}
  >
    <Send className="w-4 h-4" />
    Send SMS
  </button>
</div>

          </div>

          {/* Small note */}
          <div className={`mt-4 text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            Note: Excel/TDS/SMS buttons need corresponding Node endpoints (export/tds-export/send-sms). Search + pagination are ready.
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeMasterReport;
